<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import { formatHashrate, formatRelativeDate, getBreakpoint } from "@/utils";
import NavigationWrapper from "@/components/NavigationWrapper";
import HashrateChart from "@/components/HashrateChart";
import InfoTooltip from "@/components/InfoTooltip";

export default {
  // Included components
  components: {
    FontAwesomeIcon,
    NavigationWrapper,
    HashrateChart,
    InfoTooltip,
  },

  data() {
    return {
      // Number of workers to show on each page / current request limit
      PAGE_SIZE: 20,

      // Current request limit
      limit: 20,

      // current request offset
      offset: 0,

      // The current page number (1 indexed)
      currentPage: null,
      // current workers accessible on the page (currently or through pagination)
      currentWorkers: [],

      // total number of pages
      totalPages: 0,

      // Whether list view or card view is active
      listView: true,

      // search input
      searchQuery: "",

      // whether data is currently being fetched
      loadingState: true,
    };
  },

  computed: {
    // The current page of workers
    page() {
      return this.currentWorkers.slice(0, this.PAGE_SIZE);
    },

    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["workers", "workerCount", "workerShares", "license"]),
  },

  watch: {
    async listView(value) {
      // Display 25 miners per page if list view is active
      this.PAGE_SIZE = value ? 20 : 10;

      // recalculate pagination parameters
      this.totalPages = (this.totalPages * this.limit) / this.PAGE_SIZE; // total workers / new page size
      this.limit = value ? 20 : 10; // fetch only 10 workers for card view per page

      // recalculate current page parameters
      let page_num =
        Math.floor((this.offset + this.limit) / this.PAGE_SIZE) === 0
          ? 1
          : Math.floor((this.offset + this.limit) / this.PAGE_SIZE);
      if (page_num !== parseInt(this.currentPage)) {
        // only change route if page differs after switching
        this.currentPage = page_num;
        this.$router.replace({ path: "/workers", query: { page: page_num } });
      }

      if (value) {
        if (this.offset % 20 > 0) {
          this.offset = this.offset - 10;
        }
        // switched from card view to list view, refetch to get all 20 workers instead of just 10
        await this.fetchWorkers();
      } else {
        // when switching from list to card view, the last 10 workers in the list will no longer be visible, need to change offset
        this.offset = this.offset === 0 ? 0 : this.offset - 10;
      }
    },

    async currentPage(value) {
      // update the offset to reflect the current page and refetch
      // subtract 1 from value to get correct 0 index offset (we want no offset for page 1 and so on)
      let page_num = value === 0 ? 0 : value - 1;
      this.offset = this.limit * page_num;
      this.loadingState = true;
      await this.fetchWorkers();
      this.loadingState = false;
    },

    searchQuery(value) {
      // if additional input is being provided, reset offset
      if (value) {
        this.offset = 0;
      } else {
        this.offset = this.limit * (this.currentPage - 1);
      }
    },
  },

  async created() {
    // Determine page
    this.currentPage = this.$route.query.page || 1;
    this.loadingState = true;
    await this.getWorkerCount();
    await this.fetchWorkers();
    this.loadingState = false;
  },

  methods: {
    async fetchWorkers() {
      const inputQuery = this.searchQuery.toLowerCase();
      let result = null;
      let fields =
        "id,name,pool_configuration.worker_name,last_submitted,device.name";
      if (inputQuery) {
        result = await this.getWorkers({
          limit: this.limit,
          offset: this.offset,
          searchString: inputQuery,
          fields,
        });
      } else {
        result = await this.getWorkers({
          limit: this.limit,
          offset: this.offset,
          fields,
        });
      }

      this.currentWorkers = result.workers;
      this.totalPages = result.paging.page_count;
    },

    // Helper methods and vuex actions
    formatRelativeDate,
    formatHashrate,
    getBreakpoint,
    ...mapActions([
      "getWorkers",
      "getWorkerCount",
      "getWorkerShares",
      "getWorkerHashrate",
      "getWorkerLastSubmission",
      "getLicense",
    ]),
  },

  filters: {
    truncate(label) {
      if (label.length >= 25) {
        return (
          label.substr(0, 10) + "..." + label.substr(label.length - 10, 10)
        );
      } else {
        return label;
      }
    },
  },
};
</script>
<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/workers" active>Workers</b-breadcrumb-item>
    </template>
    <b-row class="Toolbar mb-3">
      <b-col>
        <h1>Workers</h1>
      </b-col>
      <b-col
        class="text-right"
        align-self="center"
        v-if="can.workers.edit && this.workerCount !== 0"
      >
        <!--<b-button id="listview" variant="light" :pressed.sync="listView" v-if="!getBreakpoint().xs">
          <FontAwesomeIcon v-if="!listView" icon="list" size="lg" />
          <FontAwesomeIcon v-else icon="th" size="lg" />
        </b-button>-->
        <span
          id="disabled-wrapper"
          class="d-inline-block float-right"
          tabindex="0"
        >
          <b-dropdown
            split
            right
            split-variant="primary"
            variant="primary"
            text="Add Worker"
            class="m-2 float-right"
            split-to="/workers/create"
            :disabled="
              license.max_workers != null &&
              this.workerCount >= license.max_workers
            "
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            <b-dropdown-item to="/workers/bulk-create">
              Bulk Add Workers
            </b-dropdown-item>
          </b-dropdown>
          <b-tooltip
            v-if="
              license.max_workers != null &&
              this.workerCount >= license.max_workers
            "
            target="disabled-wrapper"
          >
            You have reached the maximum {{ license.max_workers }} workers for
            your license
          </b-tooltip>
        </span>
      </b-col>
    </b-row>

    <b-row align-v="center">
      <b-col :cols="getBreakpoint().xs ? '9' : '12'" class="mb-3">
        <b-input-group>
          <b-form-input
            placeholder="Search for a worker..."
            v-model="searchQuery"
            v-debounce.fireonempty="fetchWorkers"
          ></b-form-input>
          <b-input-group-append>
            <b-button id="search-button">
              <FontAwesomeIcon icon="search" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col
        cols="3"
        align-self="center"
        class="mb-3"
        v-if="getBreakpoint().xs"
      >
        <b-button
          variant="light"
          :pressed.sync="listView"
          v-if="getBreakpoint().xs"
        >
          <FontAwesomeIcon icon="list" size="lg" />
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="this.workerCount === 0">
      <b-col>
        <b-card class="text-center py-5">
          <b-card-title>No Workers</b-card-title>
          <b-card-text>Get started by adding a worker</b-card-text>
          <b-dropdown
            split
            split-variant="primary"
            variant="primary"
            text="Add worker"
            class="m-2"
            split-to="/workers/create"
            :disabled="
              license.max_workers != null &&
              this.workerCount >= license.max_workers
            "
          >
            <b-dropdown-item to="/workers/bulk-create">
              Bulk Add Workers
            </b-dropdown-item>
          </b-dropdown>
        </b-card>
      </b-col>
    </b-row>

    <div v-else>
      <b-row v-if="loadingState">
        <b-col class="text-center mt-5">
          <div class="mb-4">
            <b-spinner
              style="width: 4rem; height: 4rem"
              variant="primary"
              label="Loading Workers..."
            ></b-spinner>
          </div>
          <b>Loading Workers...</b>
        </b-col>
      </b-row>
      <b-row v-else-if="!listView && !loadingState">
        <b-col lg="6" v-for="(worker, index) in page" :key="index">
          <b-card class="Worker-card Card-hoverable">
            <b-link :to="`/workers/${worker.id}`">
              <b-card-title>
                <div class="Worker-card-title">
                  {{ worker.name }}
                  <span class="Worker-card-link-indicator">
                    <font-awesome-icon icon="chevron-right" size="xs" />
                  </span>
                </div>
              </b-card-title>
              <b-card-sub-title class="mb-2">
                {{ worker.device.name }}
              </b-card-sub-title>
              <b-row>
                <b-col cols="6">
                  <b>Hashrate</b>
                  <InfoTooltip text="30 minute average hashrate" />
                  <p>{{ formatHashrate(worker.hashrate_30m) }}</p>
                </b-col>
                <b-col cols="6">
                  <b>Last Submission</b>
                  <p :title="worker.last_submitted">
                    {{ formatRelativeDate(worker.last_submitted) }}
                  </p>
                </b-col>
              </b-row>

              <HashrateChart
                :data="
                  workerShares[worker.id]
                    ? workerShares[worker.id].map((share) => [
                        share[0],
                        share[1] / 300,
                      ])
                    : []
                "
                :canExport="false"
              />
            </b-link>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-else-if="listView && !loadingState">
        <b-col>
          <b-table-simple responsive striped hover>
            <b-thead>
              <b-tr>
                <b-th>Name</b-th>
                <b-th>Pool Worker Name</b-th>
                <b-th>Device</b-th>
                <b-th>Hashrate</b-th>
                <b-th>Last Submission</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(worker, index) in page" :key="index">
                <b-td>
                  <b-link :to="`/workers/${worker.id}`">
                    {{ worker.name }}
                  </b-link>
                </b-td>
                <div v-if="worker.pool_configuration">
                  <b-td
                    v-if="worker.pool_configuration.worker_name.length >= 20"
                  >
                    <div
                      v-b-tooltip.hover="worker.pool_configuration.worker_name"
                    >
                      {{
                        worker.pool_configuration
                          ? worker.pool_configuration.worker_name
                          : "" | truncate
                      }}
                    </div>
                  </b-td>
                  <b-td
                    v-if="worker.pool_configuration.worker_name.length < 20"
                  >
                    <div>
                      {{
                        worker.pool_configuration
                          ? worker.pool_configuration.worker_name
                          : "" | truncate
                      }}
                    </div>
                  </b-td>
                </div>
                <div v-if="!worker.pool_configuration">
                  <b-td>None</b-td>
                </div>
                <b-td>{{ worker.device.name }}</b-td>
                <b-td>{{ formatHashrate(worker.hashrate) }}</b-td>
                <b-td>
                  {{ formatRelativeDate(worker.last_submitted) }}
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>
      </b-row>

      <b-pagination-nav
        v-if="this.totalPages > 1 && !loadingState"
        :number-of-pages="this.totalPages"
        base-url="?page="
        align="center"
        limit="7"
        size="sm"
        v-model="currentPage"
        :use-router="true"
        :hide-goto-end-buttons="this.totalPages < 3"
      ></b-pagination-nav>
    </div>
  </NavigationWrapper>
</template>

<style scoped>
.Worker-card {
  margin-bottom: 2rem;
}

.Worker-card-title {
  display: flex;
  align-items: center;
}

.Worker-card .Worker-card-link-indicator {
  display: inline-flex;
  align-items: center;

  opacity: 0;

  transition: all 200ms;
}

.Worker-card:hover .Worker-card-link-indicator {
  opacity: 1;
  margin-left: 0.35rem;
}

.Worker-card a {
  color: unset;
  font-weight: unset;
  text-decoration: unset;
}

.Worker-card:hover .Worker-card-title {
  color: #007bff;
}

#listview {
  display: inline-block;
  margin-top: 7px;
}

#search-button {
  border: 1px solid transparent;
  box-shadow: 0 0 2px 0 rgba(0, 20, 40, 0.4);
}

#list-more:hover {
  cursor: pointer;
}
</style>
